<template>
  <div class="min-h-screen">
    <AppNavbar />
    
    <!-- Hero Section -->
    <section class="page-gutter section-gap pt-32">
      <div class="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-7xl mx-auto">
        <!-- Left: Copy -->
        <div class="space-y-6">
          <h1 class="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            LINK YOUR <span class="gradient-text">INSTAGRAM POSTS</span> TO<br/>
            ANYWHERE
          </h1>

          <p class="text-muted text-lg max-w-2xl">
            Turn Instagram posts into powerful gateways. Seamlessly direct your followers to any destination—your website, shop, event, or any custom link—right from your post.
          </p>

          <div class="flex flex-wrap items-center gap-4">
            <a
              href="https://apps.apple.com/us/app/app-to-link/id6753908110"
              class="btn btn-primary"
              aria-label="Download on the App Store"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              App Store
            </a>
            <button 
              @click="showComingSoon"
              class="btn btn-ghost"
              aria-label="Get it on Google Play"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Google Play
            </button>
          </div>
        </div>

        <!-- Right: Visual -->
        <div class="lg:ml-auto lg:max-w-lg">
          <div class="card">
            <div class="aspect-[4/5] rounded-xl2 bg-gradient-to-b from-primary-600 to-accent/70 flex items-center justify-center overflow-hidden">
              <img 
                src="/social_link_hero.jpg" 
                alt="Link Instagram posts to any destination" 
                class="w-full h-full object-contain p-8"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="page-gutter section-gap bg-surface/30">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-3xl sm:text-4xl font-bold text-center mb-12">
          Why <span class="gradient-text">App to Link</span>?
        </h2>
        
        <div class="grid md:grid-cols-3 gap-6">
          <div class="card text-center group hover:scale-105 transition-transform duration-300">
            <div class="text-5xl mb-4 text-primary-500">
              <svg class="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold mb-2">Instagram Integration</h3>
            <p class="text-muted">
              Link your Instagram posts to any URL—no more "link in bio" limitations.
            </p>
          </div>

          <div class="card text-center group hover:scale-105 transition-transform duration-300">
            <div class="text-5xl mb-4 text-accent">
              <svg class="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold mb-2">Custom Destinations</h3>
            <p class="text-muted">
              Send your audience to your shop, event, blog, or any custom page with a single tap.
            </p>
          </div>

          <div class="card text-center group hover:scale-105 transition-transform duration-300">
            <div class="text-5xl mb-4 text-primary-400">
              <svg class="w-12 h-12 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17,19H7V5H17M17,1H7C5.89,1 5,1.89 5,3V21A2,2 0 0,0 7,23H17A2,2 0 0,0 19,21V3C19,1.89 18.1,1 17,1Z"/>
              </svg>
            </div>
            <h3 class="text-xl font-bold mb-2">Easy & Fast</h3>
            <p class="text-muted">
              Set up your links in seconds with our intuitive mobile app for iOS and Android.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="page-gutter section-gap">
      <div class="max-w-7xl mx-auto">
        <h2 class="text-3xl sm:text-4xl font-bold text-center mb-12">
          How It <span class="gradient-text">Works</span>
        </h2>
        
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center space-y-4">
            <div class="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center text-2xl font-bold text-white">
              1
            </div>
            <h3 class="text-xl font-bold">Download App to Link</h3>
            <p class="text-muted">
              Get the app from the App Store or Google Play and sign in with your account.
            </p>
          </div>

          <div class="text-center space-y-4">
            <div class="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center text-2xl font-bold text-white">
              2
            </div>
            <h3 class="text-xl font-bold">Share Your Post</h3>
            <p class="text-muted">
              Create a post on Instagram, then use App to Link to attach your custom link.
            </p>
          </div>

          <div class="text-center space-y-4">
            <div class="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500 to-accent flex items-center justify-center text-2xl font-bold text-white">
              3
            </div>
            <h3 class="text-xl font-bold">Drive Traffic Anywhere</h3>
            <p class="text-muted">
              Followers tap your post and are instantly taken to your chosen destination—no extra steps.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Who Is It For Section -->
    <section class="page-gutter section-gap bg-surface/30">
      <div class="max-w-5xl mx-auto">
        <div class="grid md:grid-cols-2 gap-8">
          <div class="card">
            <h3 class="text-2xl font-bold mb-6 flex items-center gap-3">
              <svg class="w-6 h-6 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M7.07,18.28C7.5,17.38 10.12,16.5 12,16.5C13.88,16.5 16.5,17.38 16.93,18.28C15.57,19.36 13.86,20 12,20C10.14,20 8.43,19.36 7.07,18.28M18.36,16.83C16.93,15.09 13.46,14.5 12,14.5C10.54,14.5 7.07,15.09 5.64,16.83C4.62,15.5 4,13.82 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,13.82 19.38,15.5 18.36,16.83M12,6C10.06,6 8.5,7.56 8.5,9.5C8.5,11.44 10.06,13 12,13C13.94,13 15.5,11.44 15.5,9.5C15.5,7.56 13.94,6 12,6M12,11A1.5,1.5 0 0,1 10.5,9.5A1.5,1.5 0 0,1 12,8A1.5,1.5 0 0,1 13.5,9.5A1.5,1.5 0 0,1 12,11Z"/>
              </svg>
              Why App to Link?
            </h3>
            <ul class="space-y-3">
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                </svg>
                No more "link in bio" hassle
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                </svg>
                Boost engagement and conversions
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                </svg>
                Simple, secure, and privacy-friendly
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-accent flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/>
                </svg>
                Analytics to track your link performance
              </li>
            </ul>
          </div>

          <div class="card">
            <h3 class="text-2xl font-bold mb-6 flex items-center gap-3">
              <svg class="w-6 h-6 text-accent" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16,13C15.71,13 15.38,13 15.03,13.05C16.19,13.89 17,15 17,16.5V19H23V16.5C23,14.17 18.33,13 16,13M8,13C5.67,13 1,14.17 1,16.5V19H15V16.5C15,14.17 10.33,13 8,13M8,11A3,3 0 0,0 11,8A3,3 0 0,0 8,5A3,3 0 0,0 5,8A3,3 0 0,0 8,11M16,11A3,3 0 0,0 19,8A3,3 0 0,0 16,5A3,3 0 0,0 13,8A3,3 0 0,0 16,11Z"/>
              </svg>
              Who Is It For?
            </h3>
            <ul class="space-y-3">
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M7.07,18.28C7.5,17.38 10.12,16.5 12,16.5C13.88,16.5 16.5,17.38 16.93,18.28C15.57,19.36 13.86,20 12,20C10.14,20 8.43,19.36 7.07,18.28M18.36,16.83C16.93,15.09 13.46,14.5 12,14.5C10.54,14.5 7.07,15.09 5.64,16.83C4.62,15.5 4,13.82 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,13.82 19.38,15.5 18.36,16.83M12,6C10.06,6 8.5,7.56 8.5,9.5C8.5,11.44 10.06,13 12,13C13.94,13 15.5,11.44 15.5,9.5C15.5,7.56 13.94,6 12,6M12,11A1.5,1.5 0 0,1 10.5,9.5A1.5,1.5 0 0,1 12,8A1.5,1.5 0 0,1 13.5,9.5A1.5,1.5 0 0,1 12,11Z"/>
                </svg>
                Influencers & Creators
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,18H6V14H12M21,14V12L20,7H4L3,12V14H4V20H14V14H18V20H20V14M20,4H4V6H20V4Z"/>
                </svg>
                Businesses & Shops
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                </svg>
                Event Organizers
              </li>
              <li class="flex items-start gap-3 text-muted">
                <svg class="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.9,17.39C17.64,16.59 16.89,16 16,16H15V13A1,1 0 0,0 14,12H8V10H10A1,1 0 0,0 11,9V7H13A2,2 0 0,0 15,5V4.59C17.93,5.77 20,8.64 20,12C20,14.08 19.2,15.97 17.9,17.39M11,19.93C7.05,19.44 4,16.08 4,12C4,11.38 4.08,10.78 4.21,10.21L9,15V16A2,2 0 0,0 11,18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
                Anyone who wants to share more!
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="page-gutter py-8 border-t border-border">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p class="text-muted text-sm">&copy; 2024 App to Link. All rights reserved.</p>
        <nav class="flex items-center gap-6 text-sm text-muted">
          <router-link to="/" class="hover:text-text transition">Home</router-link>
          <router-link to="/pricing" class="hover:text-text transition">Pricing</router-link>
          <router-link to="/privacy-policy" class="hover:text-text transition">Privacy Policy</router-link>
          <router-link to="/terms-of-service" class="hover:text-text transition">Terms of Service</router-link>
          <router-link to="/data-deletion" class="hover:text-text transition">Data Deletion</router-link>
          <a :href="infoMailto" class="hover:text-text transition">Contact</a>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script>
import AppNavbar from './AppNavbar.vue';
import emailObfuscation from '@/mixins/emailObfuscation';

export default {
  name: 'Home',
  components: { AppNavbar },
  mixins: [emailObfuscation],
  methods: {
    showComingSoon() {
      alert('Coming soon...');
    }
  }
}
</script>
